import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { RESOURCE_NAME, keys } from "./i18n";
import { type GetObjectiveQuery } from "@generated/client/graphql";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { orderPeriods } from "@app/usecases/ReportingUseCases";
import { Text } from "@design-system/Typography/Text";
import { useDisplayedPeriod } from "@app/pages/Reporting/hooks";

export const DatapointValueDisplay: FC<{
    objective: GetObjectiveQuery["objective"];
}> = ({ objective }) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { getDetailedDisplayedPeriod } = useDisplayedPeriod();

    const datapoints = useMemo(() => {
        const entityDatapoints = objective?.indicator?.datapoints
            ?.filter(
                (dp) => dp.datapointGroup?.entityId === objective?.entityId,
            )
            .filter((dp) => !dp.value?.noData)
            .filter((dp) => typeof dp.value?.float === "number");

        const sortedDatapoints = entityDatapoints?.sort((d1, d2) =>
            orderPeriods(d2.datapointGroup?.period, d1.datapointGroup?.period),
        );

        return sortedDatapoints?.map((dp) => ({
            id: dp.id,
            value: dp.value?.float,
            unit: objective.indicator?.unit?.shortName,
            period: getDetailedDisplayedPeriod(dp.datapointGroup?.period),
        }));
    }, [objective.indicator]);

    return datapoints?.length ? (
        <FlexCol className="w-[280px]" gap="2" w="full">
            <Text color="secondary">{t(keys.historical_data_title)}</Text>
            {datapoints.map((dp) => (
                <FlexRow key={dp.id} gap="8">
                    <FlexRow className="w-[148px]" gap="1" justifyContent="end">
                        <Text align="right">{dp.value}</Text>
                        <Text color="secondary" align="right">
                            {dp.unit}
                        </Text>
                    </FlexRow>
                    <Text>{dp.period}</Text>
                </FlexRow>
            ))}
        </FlexCol>
    ) : null;
};
